import type {
  CreditNoteStatus,
  DunningNoticeStatus,
  InvoiceStatus,
  PaymentOrderStatus,
} from '@osteopro/shared';

export interface InvoiceListParams {
  /** Plusieurs statuts : le serveur les accepte en liste, l'écran n'a plus à trier lui-même. */
  statuses?: readonly InvoiceStatus[];
  /** Factures d'une consultation : c'est ce qui rend la section Facturation d'une séance possible. */
  encounterId?: string;
  patientId?: string;
  from?: string;
  to?: string;
  page: number;
  /** Absente : la taille par défaut de la liste. */
  pageSize?: number;
}

/**
 * Facturation. Une seule racine pour les quatre familles de pièces : encaisser une facture change
 * son reste dû, donc la relance qui s'y adosse et l'ordonnance qui la suit. Invalider la racine
 * après une écriture est la seule façon sûre de ne pas laisser un écran en retard d'un état.
 */
export const billingKeys = {
  all: () => ['billing'] as const,
  settings: () => [...billingKeys.all(), 'settings'] as const,
  retrocession: (from: string, to: string, memberId: string | null) =>
    [...billingKeys.all(), 'retrocession', from, to, memberId] as const,
  memberTerms: (memberId: string) => [...billingKeys.all(), 'member-terms', memberId] as const,
  retrocessionRates: () => [...billingKeys.all(), 'retrocession-rates'] as const,

  invoices: () => [...billingKeys.all(), 'invoices'] as const,
  invoiceList: (params: InvoiceListParams) => [...billingKeys.invoices(), 'list', params] as const,
  invoice: (invoiceId: string) => [...billingKeys.invoices(), 'detail', invoiceId] as const,
  /** Corps prérempli du dialogue d'envoi, relu à chaque ouverture. */
  invoiceSendDraft: (invoiceId: string) =>
    [...billingKeys.invoices(), 'send-draft', invoiceId] as const,

  creditNotes: () => [...billingKeys.all(), 'credit-notes'] as const,
  creditNoteList: (params: { status?: CreditNoteStatus; encounterId?: string; page?: number }) =>
    [...billingKeys.creditNotes(), 'list', params] as const,

  dunning: () => [...billingKeys.all(), 'dunning'] as const,
  dunningList: (params: {
    status?: DunningNoticeStatus;
    invoiceId?: string;
    encounterId?: string;
    page?: number;
  }) => [...billingKeys.dunning(), 'list', params] as const,
  /** Résumé du recouvrement, qui porte aussi les badges : rafraîchi toutes les 60 s. */
  dunningSummary: () => [...billingKeys.dunning(), 'summary'] as const,
  /** Historique d'envoi d'une relance : les précédents, qu'un renvoi écrasait. */
  dunningSendings: (dunningId: string | null) =>
    [...billingKeys.dunning(), 'sendings', dunningId] as const,
  /** Modèles de courriel du cabinet, un par niveau. */
  dunningTemplates: () => [...billingKeys.dunning(), 'templates'] as const,
  /** Factures impayées et leur étape suivante, calculée par le serveur. */
  dunningUnpaid: () => [...billingKeys.dunning(), 'unpaid'] as const,

  paymentOrders: () => [...billingKeys.all(), 'payment-orders'] as const,
  paymentOrderList: (params: {
    status?: PaymentOrderStatus;
    encounterId?: string;
    page?: number;
  }) => [...billingKeys.paymentOrders(), 'list', params] as const,
};
